import {
  Box,
  HStack,
  Text,
  IconButton,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import { FiChevronLeft } from "react-icons/fi";
import { useState, useEffect, useRef } from "react";
import {
  useLunaStore,
  useLunaSession,
  useGetLunaRequests,
} from "state/luna";
import { useNavigate } from "react-router-dom";
import { TypedText } from "./TypedText";
import { LunaMark } from "components/ds/LunaMark";
import { useGovernedMotion } from "components/ds/frameGovernor";

const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
`;

const BouncingDots = () => {
  /* the dots free-run for as long as a session has no title — govern them */
  const rootRef = useGovernedMotion<HTMLDivElement>();
  return (
    <HStack ref={rootRef} spacing="2px">
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          w="4px"
          h="4px"
          borderRadius="full"
          bg="text.body.tertiary"
          animation={`${bounce} 1s ease-in-out infinite`}
          sx={{
            animationDelay: `${i * 0.15}s`,
            /* the governor skips reduced-motion, so the bounce must not
               free-run there either; the dots stay visible, just still */
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        />
      ))}
    </HStack>
  );
};

interface LunaHeaderProps {
  sessionId: string | null;
}

export const LunaHeader = ({ sessionId }: LunaHeaderProps) => {
  const navigate = useNavigate();
  const session = useLunaSession(sessionId);
  const { data: requests } = useGetLunaRequests();
  const disconnectFromSession = useLunaStore(
    (state) => state.disconnectFromSession,
  );
  const setActiveSession = useLunaStore((state) => state.setActiveSession);

  // Track title animation state
  const [isAnimating, setIsAnimating] = useState(false);
  const [animatingTitle, setAnimatingTitle] = useState<string | null>(null);
  const prevTitleRef = useRef<string | undefined>(undefined);
  const prevSessionIdRef = useRef<string | null>(null);

  // Use session title from socket, fall back to requests list title
  const requestTitle = requests?.find((r) => r.sessionId === sessionId)?.title;
  const currentTitle = session?.title || requestTitle || undefined;

  // Animate title whenever it changes (including first appearance)
  useEffect(() => {
    // Reset when session changes
    if (sessionId !== prevSessionIdRef.current) {
      prevSessionIdRef.current = sessionId;
      prevTitleRef.current = undefined;
      setIsAnimating(false);
      setAnimatingTitle(null);
    }

    // Animate if we have a new title that's different from previous
    if (currentTitle && currentTitle !== prevTitleRef.current) {
      setAnimatingTitle(currentTitle);
      setIsAnimating(true);
    }

    prevTitleRef.current = currentTitle;
  }, [currentTitle, sessionId]);

  const handleBack = () => {
    if (sessionId) {
      disconnectFromSession(sessionId);
    }
    setActiveSession(null);
    navigate("/");
  };

  return (
    <Box
      bg="bg.page"
      borderBottom="1px solid"
      borderColor="border.default"
      px="xl"
      h="56px"
      w="full"
      flexShrink={0}
    >
      <HStack spacing="sm" h="100%" align="center">
        {/* Back button and session title */}
        <IconButton
          aria-label="Back to requests"
          icon={<FiChevronLeft />}
          variant="ghost"
          size="sm"
          onClick={handleBack}
          color="text.body.tertiary"
          minW="auto"
        />
        <LunaMark size={18} animated={false} />
        {isAnimating && animatingTitle ? (
          <TypedText
            text={animatingTitle}
            color="text.body.primary"
            fontSize="md"
            fontWeight="medium"
            onComplete={() => setIsAnimating(false)}
          />
        ) : currentTitle && currentTitle === prevTitleRef.current ? (
          <Text
            color="text.body.primary"
            fontSize="md"
            fontWeight="medium"
            noOfLines={1}
          >
            {currentTitle}
          </Text>
        ) : (
          <BouncingDots />
        )}
      </HStack>
    </Box>
  );
};
